import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Theme
import materialTheme from './theme/materialTheme';

// Query Client
import { queryClient } from './lib/queryClient';

// Context
import { AuthProvider } from './context/AuthContext';
import { AppDataProvider } from './context/AppDataContext';

// Services
import './services/barcodeWorkerService'; // Initialize barcode worker service

// Components
import LoginForm from './components/Auth/LoginForm';
import DashboardLayout from './components/Layout/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import MobileAwareRedirect from './components/MobileAwareRedirect';

// Pages
import Dashboard from './components/Dashboard/Dashboard';
import InventoryImportList from './components/InventoryImport/InventoryImportList';
import InventoryImportDetailPage from './components/InventoryImport/InventoryImportDetailPage';
import InventoryImportCreatePage from './components/InventoryImport/InventoryImportCreatePage';
import NotificationsList from './components/Notifications/NotificationsList';
import InventoryTransferList from './components/InventoryTransfer/InventoryTransferList';
import InventoryTransferDetailPage from './components/InventoryTransfer/InventoryTransferDetailPage';
import InventoryTransferCreatePage from './components/InventoryTransfer/InventoryTransferCreatePage';
import InventoryCountHistory from './components/InventoryAudit/InventoryCountHistory';
import InventoryCountDetailPage from './components/InventoryAudit/InventoryCountDetailPage';
import AuditHistory from './components/InventoryAudit/AuditHistory';
import ReportsDashboard from './components/Reports/ReportsDashboard';
import Settings from './components/Settings/Settings';
import MobileDetectionDemo from './components/Demo/MobileDetectionDemo';
import BarcodeWorkerDemo from './components/Demo/BarcodeWorkerDemo';
import TagTimeline from './components/TagTimeline/TagTimeline';
import UserRolesPermissions from './components/UserRolesPermissions/UserRolesPermissions';
import Documents from './components/Documents/Documents';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={materialTheme}>
        <CssBaseline />
        <AppDataProvider>
          <AuthProvider>
            <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginForm />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/" element={<MobileAwareRedirect />} />
              
              <Route path="/inventory-import" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryImportList />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/inventory-import/create/:orderId?" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryImportCreatePage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/inventory-import/:orderId" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryImportDetailPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <NotificationsList />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/inventory-transfer" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryTransferList />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/inventory-transfer/create/:transferId?" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryTransferCreatePage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/inventory-transfer/:transferId" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryTransferDetailPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/inventory-count-history" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryCountHistory />
                  </DashboardLayout>
                </ProtectedRoute>
              } />

              <Route path="/inventory-count-history/:id" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InventoryCountDetailPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/audit-history" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AuditHistory />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/reports" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ReportsDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/user-roles-permissions" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <UserRolesPermissions />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/documents" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Documents />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Settings />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/tag-timeline" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <TagTimeline />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/mobile-demo" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <MobileDetectionDemo />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/barcode-demo" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <BarcodeWorkerDemo />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              {/* Catch all route */}
              <Route path="*" element={<MobileAwareRedirect />} />
            </Routes>
          </Router>
        </AuthProvider>
      </AppDataProvider>
      </ThemeProvider>
      {/* React Query Devtools - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )}
    </QueryClientProvider>
  );
}

export default App;
